import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../hooks/useAuth";
import { AuthService } from "../utils/auth";
import { useTranslation } from "../hooks/useTranslation";
import OAuthButtons from "./OAuthButtons";
import AuthShell, { authError, authField, authLabel } from "../v2/pages/AuthShell";
import { Arrow } from "../v2/components/Primitives";

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const isFr = currentLanguage !== "en";
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOpenForgot = () => {
    setForgotEmail(formData.email || "");
    setForgotError("");
    setForgotSuccess("");
    setShowForgot(true);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");

    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      const res = await fetch(`${apiUrl}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      // We don't reveal existence; treat 200 as success
      if (res.ok) {
        setShowForgot(false);
        const emailForQuery = encodeURIComponent(forgotEmail.trim());
        navigate(
          `/${currentLanguage}/verify-email?email=${emailForQuery}&flow=reset`,
        );
      } else {
        try {
          const data = await res.json();
          setForgotError(data?.message || t("auth.login.genericError"));
        } catch {
          setForgotError(t("auth.login.genericError"));
        }
      }
    } catch (err) {
      setForgotError(t("auth.login.serverError"));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const apiUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const payload = (data && (data.data || data)) || {};
        const tokens = payload.tokens ||
          payload.data?.tokens || {
            accessToken: payload.accessToken,
            refreshToken: payload.refreshToken,
          };

        if (tokens?.accessToken && tokens?.refreshToken) {
          AuthService.setTokens(tokens);
        }

        let userProfile = payload.user || payload.data?.user || data.user;

        if (!userProfile && tokens?.accessToken) {
          try {
            userProfile = await AuthService.fetchProfile();

            if (!userProfile) {
              const profileResponse = await fetch(`${apiUrl}/auth/profile`, {
                headers: { Authorization: `Bearer ${tokens.accessToken}` },
              });

              if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                userProfile =
                  profileData.data?.user ||
                  profileData.user ||
                  profileData.data;
                if (userProfile) {
                  AuthService.setUserProfile(userProfile);
                }
              }
            }
          } catch (profileError) {
            console.error("Error fetching user profile:", profileError);
          }
        } else if (userProfile) {
          AuthService.setUserProfile(userProfile);
        }

        if (userProfile) {
          login(tokens, userProfile);
          navigate(`/${currentLanguage}/dashboard`);
        } else {
          setError(t("auth.login.fetchUserError"));
        }
      } else {
        setError(data.message || t("auth.login.invalidCreds"));
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(t("auth.login.serverError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.login.title")}
      statement={
        isFr ? (
          <>
            Vos paires vous attendent, <em className="italic text-tangerine">propres.</em>
          </>
        ) : (
          <>
            Your pairs are waiting, <em className="italic text-tangerine">clean.</em>
          </>
        )
      }
      note={
        isFr
          ? "Suivez vos commandes, vos devis et vos abonnements depuis votre espace."
          : "Track your orders, estimates and subscriptions from your account."
      }
      description={
        isFr
          ? "Connectez-vous à votre espace client Lave Tes Baskets pour suivre vos commandes de nettoyage de baskets, vos devis et vos abonnements."
          : "Log in to your Lave Tes Baskets account to track your sneaker cleaning orders, estimates and subscriptions."
      }
      img="/illustrations/landing/login.webp"
    >
      <div className="v2-reveal">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
          {isFr ? "Espace client" : "Account"}
        </div>
        <h1 className="mt-3 font-display text-[clamp(2.2rem,4vw,3.25rem)] leading-[0.98] tracking-[-0.02em]">
          {t("auth.login.title")}
        </h1>
        <p className="mt-3 text-sm text-ink/60">{t("auth.login.subtitle")}</p>
      </div>

      <div className="v2-reveal mt-8" data-delay="1">
        <OAuthButtons mode="login" />
      </div>

      <form onSubmit={handleSubmit} className="v2-reveal mt-6 space-y-7" data-delay="2">
        {error && (
          <p className="rounded-xl border border-tangerine/40 bg-tangerine/10 px-4 py-3 text-sm text-ink">
            {error}
          </p>
        )}

        <div>
          <label htmlFor="email" className={authLabel}>
            {t("auth.login.emailLabel")}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={authField}
            placeholder={t("auth.login.emailPlaceholder")}
            autoComplete="email"
            required
          />
        </div>

        <div>
          <label htmlFor="password" className={authLabel}>
            {t("auth.login.passwordLabel")}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`${authField} pr-10`}
              placeholder={t("auth.login.passwordPlaceholder")}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center text-ink/40 hover:text-ink"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <label className="flex cursor-pointer items-center gap-2 text-ink/70">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              className="h-4 w-4 accent-[#0B0B0C]"
            />
            {t("auth.login.rememberMe")}
          </label>
          <button type="button" onClick={handleOpenForgot} className="v2-link text-ink/70 hover:text-ink">
            {t("auth.login.forgotPassword")}
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="v2-btn v2-btn-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("auth.login.submitting")}
            </>
          ) : (
            <>
              {t("auth.login.submit")}
              <Arrow />
            </>
          )}
        </button>
      </form>

      <p className="v2-reveal mt-8 text-sm text-ink/60" data-delay="3">
        {`${t("auth.login.noAccount")} `}
        <Link to={`/${currentLanguage}/signup`} className="v2-link text-ink">
          {t("auth.login.createAccount")}
        </Link>
      </p>

      {/* Forgot password */}
      {showForgot && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-bone p-6 text-ink sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <h2 className="font-display text-3xl leading-tight">{t("auth.login.modalTitle")}</h2>
              <button
                onClick={() => setShowForgot(false)}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-ink/20 hover:bg-ink hover:text-bone"
                aria-label={t("auth.login.cancel")}
              >
                ✕
              </button>
            </div>
            <p className="mt-3 text-sm text-ink/60">{t("auth.login.modalInstruction")}</p>
            {forgotError && <p className={authError}>{forgotError}</p>}
            {forgotSuccess && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-teal-deep">{forgotSuccess}</p>
            )}
            <form onSubmit={handleForgotSubmit} className="mt-6 space-y-6">
              <div>
                <label htmlFor="forgot-email" className={authLabel}>
                  {t("auth.login.emailLabel")}
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className={authField}
                  placeholder={t("auth.login.emailPlaceholder")}
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="v2-btn v2-btn-ghost !border-ink/25 !text-ink"
                >
                  {t("auth.login.cancel")}
                </button>
                <button type="submit" disabled={forgotLoading} className="v2-btn v2-btn-ink disabled:opacity-60">
                  {forgotLoading ? t("auth.login.sending") : t("auth.login.sendOtp")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AuthShell>
  );
};

export default Login;
